import { useEffect, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
} from "react-bootstrap";

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "./config";
import AdminHeader from "./AdminHeader";

import {
  encryptValue,
  decryptValue,
} from "./utils/crypto";

function Dashboard() {
  const currentDate = new Date();

  const [date, setDate] = useState(
    currentDate.getDate()
  );

  const [month, setMonth] = useState("jun");

  const [year, setYear] = useState(
    currentDate.getFullYear()
  );

  const [number, setNumber] = useState("");

  const [records, setRecords] = useState([]);

  const months = [
    { label: "January", value: "jan" },
    { label: "February", value: "feb" },
    { label: "March", value: "mar" },
    { label: "April", value: "apr" },
    { label: "May", value: "may" },
    { label: "June", value: "jun" },
    { label: "July", value: "jul" },
    { label: "August", value: "aug" },
    { label: "September", value: "sep" },
    { label: "October", value: "oct" },
    { label: "November", value: "nov" },
    { label: "December", value: "dec" },
  ];

  const [saving, setSaving] = useState(false);

  const numberRef = useRef();

  useEffect(() => {
    const currentMonth =
      months[new Date().getMonth()].value;

    setMonth(currentMonth);
  }, []);

  const fetchMonthData = async () => {
    try {
      const docRef = doc(
        db,
        "chart_data",
        year.toString()
      );

      const snapshot = await getDoc(docRef);

      if (!snapshot.exists()) {
        setRecords([]);
        return;
      }

      const data = snapshot.data();

      const monthData =
        data?.[month] || {};

      const formatted = Object.entries(
        monthData
      )
        .map(([day, item]) => {

          let decrypted = "";

          try {
            decrypted = decryptValue(item.number);
          } catch {
            decrypted = item.number;
          }

          return {
            day,
            value: decrypted,
            time: item.time?.toDate
              ? item.time.toDate()
              : null,
          };

        })
        .sort(
          (a, b) =>
            Number(b.day) -
            Number(a.day)
        );

      setRecords(formatted);
    } catch (error) {
      console.log(error);
    }
  }


  useEffect(() => {
    fetchMonthData();
  }, [month, year]);

  const handleSave = async () => {
    if (!date) {
      alert("Please enter date");
      return;
    }

    if (number.length !== 2) {
      alert(
        "Number must be 2 or 3 digits"
      );
      return;
    }

    try {
      const encrypted =
        encryptValue(number);

      const resultData = {
        number: encrypted,
        time: serverTimestamp(),
      };

      const docRef = doc(
        db,
        "chart_data",
        year.toString()
      );

      const snapshot =
        await getDoc(docRef);

      // New Year Document
      if (!snapshot.exists()) {
        await setDoc(docRef, {
          [month]: {
            [date]: resultData,
          },
        });

        setDate(date + 1);
        await fetchMonthData();

        alert(
          "Number Saved Successfully"
        );

        numberRef.current.focus();

        return;
      }

      const data = snapshot.data();

      const existingValue =
        data?.[month]?.[date];

      // Record already exists
      if (existingValue) {
        const password = prompt(
          `A number already exists for ${date}-${month}-${year}.\n\nEnter Override Password:`
        );

        if (
          password !== "123456"
        ) {
          alert(
            "Invalid password. Update cancelled."
          );
          return;
        }
      }

      await updateDoc(docRef, {
        [`${month}.${date}`]: resultData,
      });

      setDate(date + 1);

      await fetchMonthData();

      alert(
        existingValue
          ? "Number Updated Successfully"
          : "Number Saved Successfully"
      );

    } catch (error) {
      console.log(error);
      alert("Failed to save");
    }
  }

  const getMaxDays = () => {
    if (["apr", "jun", "sep", "nov"].includes(month))
      return 30;

    if (month === "feb")
      return 29;

    return 31;
  };


  return (
    <>
      <AdminHeader />

      <Container fluid="lg" className="py-4">
        <div className="mb-4">
          <h3>Dashboard</h3>

          <blockquote>
            Add daily numbers and
            manage monthly records.
          </blockquote>
        </div>

        {/* Stats */}

        <Row className="g-4 mb-4">
          <Col md={6}>
            <Card className="shadow-lg border-0">
              <Card.Body>
                <blockquote>
                  Current Year
                </blockquote>

                <h3 className="mb-0">
                  {year}
                </h3>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="shadow-lg border-0">
              <Card.Body>
                <blockquote>
                  Current Month
                </blockquote>

                <h3 className="mb-0 text-capitalize">
                  {month}
                </h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Add Form */}

        <Card className="shadow-lg border-0 mb-4">
          <Card.Body>

            <h3 className="mb-4">
              Add Daily Number
            </h3>

            <Row className="g-3">
              <Col xs={12} md={3}>
                <Form.Label>
                  Date
                </Form.Label>

                <Form.Control
                  type="number"
                  min="1"
                  max={getMaxDays()}
                  value={date}
                  onChange={(e) =>
                    setDate(
                      e.target.value
                    )
                  }
                />
              </Col>

              <Col xs={12} md={3}>
                <Form.Label>
                  Month
                </Form.Label>

                <Form.Select
                  value={month}
                  onChange={(e) =>
                    setMonth(
                      e.target.value
                    )
                  }
                >
                  {months.map(
                    (item) => (
                      <option
                        key={item.value}
                        value={
                          item.value
                        }
                      >
                        {item.label}
                      </option>
                    )
                  )}
                </Form.Select>
              </Col>

              <Col xs={12} md={3}>
                <Form.Label>
                  Year
                </Form.Label>

                <Form.Control
                  type="number"
                  value={year}
                  onChange={(e) =>
                    setYear(
                      e.target.value
                    )
                  }
                />
              </Col>

              <Col xs={12} md={3}>
                <Form.Label>
                  Number
                </Form.Label>

                <Form.Control
                  type="text"
                  maxLength={3}
                  placeholder="458"
                  value={number}
                  onChange={(e) =>
                    setNumber(
                      e.target.value.replace(
                        /\D/g,
                        ""
                      )
                    )
                  }
                />
              </Col>
            </Row>

            <Button
              variant="dark"
              className="mt-4 px-4 shadow-lg"
              onClick={handleSave}
              disabled={saving}
            >
              {
                saving
                  ?
                  "Saving..."
                  :
                  "Save Number"
              }
            </Button>
          </Card.Body>
        </Card>

        {/* Current Month Records */}

        <Card className="shadow-lg border-0">
          <Card.Body>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 className="mb-0">
                Current Month Records
              </h3>
            </div>

            <div className="table-responsive">
              <Table
                bordered
                hover
                className="align-middle"
              >
                <thead>
                  <tr>
                    <th width="120">
                      Date
                    </th>

                    <th>
                      Number
                    </th>

                    <th width="200">
                      Updated Time
                    </th>

                    {/* <th width="120">
                      Action
                    </th> */}
                  </tr>
                </thead>

                <tbody>

                  {records.length >
                    0 ? (
                    records.map(
                      (item) => (
                        <tr
                          key={
                            item.day
                          }
                        >
                          <td>
                            {item.day}
                          </td>

                          <td className="fw-bold">
                            {item.value}
                          </td>

                          <td>
                            {item.time
                              ? item.time.toLocaleString("en-IN")
                              : "--"}
                          </td>

                          {/* <td>
                            <Button
                              size="sm"
                              variant="outline-dark"
                            >
                              Edit
                            </Button>
                          </td> */}
                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan="3"
                        className="text-center py-4"
                      >
                        No Records Found
                      </td>
                    </tr>
                  )}

                </tbody>
              </Table>
            </div>

          </Card.Body>
        </Card>
      </Container>
    </>
  );
}

export default Dashboard;